import { initiateSso, SsoPortal } from '@verifime/cognito-passwordless-auth';
import { useState } from 'react';

export type TGoogleSsoSubmitEmail = (email: string) => Promise<'MAGIC_LINK' | 'LOGIN' | 'ERROR'>;

// Shared by onContinue and onResend so the two failure paths (an ERROR outcome and a thrown
// rejection) always read the same words, rather than the two call sites drifting apart over time.
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export type TUseGoogleSsoSignUpParams = {
  submitEmail: TGoogleSsoSubmitEmail;
  initialEmail: string;
  emailLocked: boolean;
  ssoOrigin: string;
  ssoReturnPath: string;
  portalEnv: SsoPortal | undefined;
  onAuthMethodSelected: (method: 'GOOGLE' | 'MAGIC_LINK') => void;
  onLoginRedirect: (email: string) => void;
};

export type TUseGoogleSsoSignUp = {
  email: string;
  emailLocked: boolean;
  display: 'ENTER_EMAIL' | 'SENT_EMAIL';
  isSubmitting: boolean;
  googleLoading: boolean;
  error: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  onGoogle: () => void;
  /** Re-runs the same submitEmail call that produced the SENT_EMAIL screen, for the LinkSentResend
   *  affordance rendered there. Unlike onContinue, a resend never re-records the auth-method
   *  selection (the customer already made that choice once); it does still redirect on a LOGIN
   *  outcome, the same way onContinue does, because that outcome means the backend now considers
   *  this email an existing session regardless of which call asked - leaving the customer on a
   *  stale "check your email" screen at that point would be the wrong screen, not a harmless one. */
  onResend: () => void;
  /** The moment the last successful send happened: set once, alongside the SENT_EMAIL transition
   *  in onContinue, and again in onResend but ONLY on a MAGIC_LINK outcome (an ERROR outcome sent
   *  nothing; a LOGIN outcome redirects away). This hook is the layer that actually fires the
   *  send (via `submitEmail`), so it is the only layer that genuinely knows when that was - see
   *  GoogleSsoSignUpView's own doc comment on `expiresAt` for why the view derives its value from
   *  this rather than computing its own. Null before any send. */
  sentAt: Date | null;
};

export function useGoogleSsoSignUp(params: TUseGoogleSsoSignUpParams): TUseGoogleSsoSignUp {
  const {
    submitEmail,
    initialEmail,
    emailLocked,
    ssoOrigin,
    ssoReturnPath,
    portalEnv,
    onAuthMethodSelected,
    onLoginRedirect,
  } = params;

  const [email, setEmail] = useState(initialEmail);
  const [display, setDisplay] = useState<'ENTER_EMAIL' | 'SENT_EMAIL'>('ENTER_EMAIL');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentAt, setSentAt] = useState<Date | null>(null);

  const onContinue = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const outcome = await submitEmail(email);

      if (outcome === 'ERROR') {
        setError(GENERIC_ERROR_MESSAGE);
        return;
      }

      // Record magic-link selection for both the email-sent and existing-user (LOGIN) outcomes.
      onAuthMethodSelected('MAGIC_LINK');

      if (outcome === 'LOGIN') {
        onLoginRedirect(email);
      } else {
        setDisplay('SENT_EMAIL');
        setSentAt(new Date());
      }
    } catch (err) {
      console.error('[useGoogleSsoSignUp] submitEmail failed:', err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResend = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const outcome = await submitEmail(email);

      if (outcome === 'ERROR') {
        setError(GENERIC_ERROR_MESSAGE);
      } else if (outcome === 'LOGIN') {
        onLoginRedirect(email);
      } else {
        // A fresh link was genuinely minted, so the expiry the view derives from sentAt must
        // move forward with it - a re-render with no fresh send must NOT (see this file's own
        // `sentAt` doc comment above).
        setSentAt(new Date());
      }
    } catch (err) {
      console.error('[useGoogleSsoSignUp] resend failed:', err);
      setError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const redirectUrl = await initiateSso({ provider: 'google', origin: ssoOrigin, returnPath: ssoReturnPath, portalEnv });
      onAuthMethodSelected('GOOGLE');
      // googleLoading intentionally stays true here: the browser is navigating
      // away to the SSO redirect, so clearing it would only flash the spinner off.
      window.location.assign(redirectUrl);
    } catch (err) {
      console.error('[useGoogleSsoSignUp] initiateSso failed:', err);
      setError('Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  return {
    email,
    emailLocked,
    display,
    isSubmitting,
    googleLoading,
    error,
    onEmailChange: setEmail,
    onContinue,
    onGoogle,
    onResend,
    sentAt,
  };
}
